import { Box, Button } from '@mui/material'
import { forwardRef, useEffect } from 'react'
import { Divider, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { StoreUserInfoFragment } from '@/clients/utoniq-core/schema'
import { handleError, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import { individualUseFullNameKatakana } from '@/core/individual'
import { validateFullName, validatePhoneNumber } from '@/core/validators'

export type Props = {
  deliveryInfo: StoreUserInfoFragment
  onSubmitSetting: (inputs: SettingValues) => Promise<void | string | boolean>
}

export type SettingValues = {
  phoneNumber: string
  fullName: string
}

const schema: UseFormProps['schema'] = o => ({
  phoneNumber: validatePhoneNumber(o, 'storeUserInfo.phoneNumber'),
  fullName: validateFullName(
    o,
    individualUseFullNameKatakana()
      ? 'storeUserInfo.fullNameKatakana'
      : 'storeUserInfo.fullName'
  ),
})

export const StoreUserInfoForm: Organism<Props> = ({
  deliveryInfo,
  onSubmitSetting,
}) => {
  const { t } = useTranslation('storeUserInfo')

  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    reset,
    setError,
  } = useForm<SettingValues>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    if (!deliveryInfo) return
    const { phoneNumber, fullName } = deliveryInfo

    reset({
      phoneNumber: phoneNumber ?? '',
      fullName: fullName ?? '',
    })
  }, [deliveryInfo, reset])

  const onSubmit = handleSubmit(values =>
    onSubmitSetting(values)
      .then(
        res =>
          res !== 'verifyPhoneNumber' &&
          showMessage(t('submitSuccess'), 'success')
      )
      .catch(e => handleError<SettingValues>(e, setError, showError))
  )

  return (
    <Form onSubmit={onSubmit} sx={{ pt: 3 }}>
      <Box>
        <TextInputCell
          type="text"
          error={errors?.fullName}
          {...register('fullName')}
          titleProps={{
            children: t(
              individualUseFullNameKatakana()
                ? 'fullNameTitleKatakana'
                : 'fullNameTitle'
            ),
          }}
          textFieldProps={{
            placeholder: t(
              individualUseFullNameKatakana()
                ? 'fullNamePlaceholderKatakana'
                : 'fullNamePlaceholder'
            ),
          }}
        />
      </Box>
      <DividerCell />
      <TextInputCell
        type="tel"
        error={errors?.phoneNumber}
        {...register('phoneNumber')}
      />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('storeUserInfo')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('storeUserInfo')
  return (
    <CellFrame sx={{ py: 5, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const DividerCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Divider />
    </CellFrame>
  )
}

const defaultValues: SettingValues = {
  phoneNumber: '',
  fullName: '',
}
